/**
 * RandomDraw — refined game UX (PRD §18, post-audit overhaul).
 *
 * Audit fixes: cards now have depth (gradient backs, shadow, emblem), a
 * smoother settle instead of the nervous 60ms random jumps, a polished flip +
 * glow reveal, i18n button (no emoji), and a win celebration. Winner decided
 * upfront via weighted `pickPrize`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '@/lib/i18n/useT';
import type { GamePlayProps, WheelItem } from './pickPrize';
import { pickPrize } from './pickPrize';
import { GameStage } from './_shared/GameStage';
import { GameButton } from './_shared/GameButton';
import { PrizeSymbol } from './_shared/PrizeSymbol';
import { useGameSound } from './_shared/useGameSound';
import { useHaptics } from './_shared/useHaptics';
import { useCelebration } from './_shared/Celebration';
import { usePrefersReducedMotion } from './_shared/usePrefersReducedMotion';
import { readableText, shade, tint, withAlpha } from './_shared/colors';

const CARD_W = 76;
const CARD_H = 108;
const FAN_W = 300;
const FAN_H = 210;

interface CardPos {
  x: number;
  y: number;
  rot: number;
}

export function RandomDraw({ items, primary, accent, onWin }: GamePlayProps) {
  const t = useT();
  const sound = useGameSound();
  const { vibrate } = useHaptics();
  const celebration = useCelebration();
  const reduced = usePrefersReducedMotion();

  const valid = useMemo(() => items.filter((i) => i.available > 0), [items]);
  const cardCount = Math.min(valid.length, 6);

  const initialPositions: CardPos[] = useMemo(() => {
    return Array.from({ length: cardCount }, (_, i) => {
      const tt = cardCount === 1 ? 0.5 : i / (cardCount - 1);
      return {
        x: 12 + tt * (FAN_W - CARD_W - 24),
        y: (FAN_H - CARD_H) / 2 - 18 + Math.abs(tt - 0.5) * 28,
        rot: (tt - 0.5) * 24,
      };
    });
  }, [cardCount]);

  const [positions, setPositions] = useState<CardPos[]>(initialPositions);
  const [phase, setPhase] = useState<'idle' | 'shuffling' | 'revealing' | 'revealed'>('idle');
  const [revealedIdx, setRevealedIdx] = useState<number>(-1);
  const winnerRef = useRef<WheelItem | null>(null);
  const rafRef = useRef<number | null>(null);
  const shuffleTickRef = useRef(0);

  useEffect(() => setPositions(initialPositions), [initialPositions]);

  function draw() {
    if (phase !== 'idle' || valid.length === 0) return;
    const winnerIdx = pickPrize(valid);
    if (winnerIdx < 0) return;
    winnerRef.current = valid[winnerIdx];
    setRevealedIdx(winnerIdx % cardCount);
    setPhase('shuffling');
    sound.play('spinUp');
    vibrate('bump');

    const shuffleTotal = reduced ? 700 : 1700;
    const start = performance.now();
    // Smooth-ish settle: move each card to a fresh random spot every ~140ms
    // and let CSS transitions glide between them (no per-frame jitter).
    let targets = randomSpread(cardCount);
    let lastSwap = 0;
    const frame = (now: number) => {
      const elapsed = now - start;
      if (elapsed - lastSwap > 140) {
        lastSwap = elapsed;
        targets = randomSpread(cardCount);
        setPositions(targets);
        if (now - shuffleTickRef.current > 120) {
          shuffleTickRef.current = now;
          sound.play('tick');
          vibrate('tick');
        }
      }
      if (elapsed < shuffleTotal) {
        rafRef.current = requestAnimationFrame(frame);
      } else {
        setPositions(initialPositions);
        setPhase('revealing');
        setTimeout(() => {
          setPhase('revealed');
          celebration.fire();
          sound.play('win');
          vibrate('win');
          if (winnerRef.current) setTimeout(() => onWin(winnerRef.current!), 1000);
        }, reduced ? 350 : 600);
      }
    };
    rafRef.current = requestAnimationFrame(frame);
  }

  useEffect(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  if (valid.length === 0) {
    return (
      <div className="rounded-2xl bg-black/40 p-8 text-center">
        <p className="font-bold">{t('publicSurvey.wheel.emptyTitle')}</p>
        <p className="mt-1 text-sm opacity-70">{t('publicSurvey.wheel.emptyBody')}</p>
      </div>
    );
  }

  return (
    <GameStage
      primary={primary}
      accent={accent}
      sound={sound}
      celebration={celebration}
      footer={
        <GameButton accent={accent} busy={phase === 'shuffling'} onClick={draw}>
          {phase === 'idle'
            ? t('publicSurvey.games.randomDraw.action')
            : phase === 'shuffling'
              ? t('publicSurvey.games.randomDraw.busy')
              : t('publicSurvey.games.randomDraw.done')}
        </GameButton>
      }
    >
      <div
        className="relative"
        style={{ width: FAN_W, height: FAN_H, perspective: 900 }}
      >
        {positions.map((pos, i) => {
          const isRevealed = phase !== 'idle' && phase !== 'shuffling' && i === revealedIdx;
          const prize = valid[i % valid.length];
          return (
            <div
              key={i}
              className="absolute"
              style={{
                width: CARD_W,
                height: CARD_H,
                transitionProperty: 'transform',
                transitionDuration: phase === 'shuffling' ? '150ms' : '520ms',
                transitionTimingFunction: 'cubic-bezier(.22,.7,.3,1)',
                transformStyle: 'preserve-3d',
                zIndex: isRevealed ? 10 : 1,
                transform: isRevealed
                  ? `translate(${(FAN_W - CARD_W * 1.45) / 2}px, ${(FAN_H - CARD_H * 1.45) / 2}px) scale(1.45) rotateY(${phase === 'revealing' ? 180 : 360}deg)`
                  : `translate(${pos.x}px, ${pos.y}px) rotate(${pos.rot}deg)`,
                filter: isRevealed ? `drop-shadow(0 0 16px ${withAlpha(accent, 0.7)})` : undefined,
              }}
            >
              {/* Card back */}
              <div
                className="absolute inset-0 flex items-center justify-center rounded-xl"
                style={{
                  background: `linear-gradient(150deg, ${tint(primary, 0.12)}, ${shade(primary, 0.55)})`,
                  border: `2px solid ${accent}`,
                  boxShadow: `0 6px 14px -4px ${withAlpha('#000', 0.7)}, inset 0 1px 0 ${withAlpha('#fff', 0.2)}`,
                  backfaceVisibility: 'hidden',
                  transform: isRevealed ? 'rotateY(180deg)' : 'rotateY(0deg)',
                  transition: 'transform 520ms',
                }}
              >
                <div
                  className="grid h-9 w-9 place-items-center rounded-full text-lg font-black"
                  style={{ background: withAlpha(accent, 0.25), color: tint(accent, 0.4), border: `1.5px solid ${withAlpha(accent, 0.6)}` }}
                >
                  ?
                </div>
              </div>
              {/* Card front */}
              <div
                className="absolute inset-0 flex flex-col items-center justify-center gap-1 rounded-xl bg-white p-1.5"
                style={{
                  border: `2px solid ${accent}`,
                  color: shade(primary, 0.8),
                  boxShadow: `0 6px 14px -4px ${withAlpha('#000', 0.7)}`,
                  backfaceVisibility: 'hidden',
                  transform: isRevealed ? 'rotateY(0deg)' : 'rotateY(180deg)',
                  transition: 'transform 520ms',
                }}
              >
                <PrizeSymbol item={prize} index={i} size={40} primary={primary} accent={accent} />
                <span className="line-clamp-2 text-center text-[10px] font-bold leading-tight">
                  {prize.name}
                </span>
              </div>
            </div>
          );
        })}
        {phase === 'revealed' && winnerRef.current && (
          <div className="absolute inset-x-0 bottom-0 text-center">
            <span
              className="rounded-full px-3 py-0.5 text-sm font-extrabold"
              style={{ background: accent, color: readableText(accent), animation: 'game-pop-in 320ms both' }}
            >
              {winnerRef.current.name}
            </span>
          </div>
        )}
      </div>
    </GameStage>
  );
}

/** A fresh spread of card positions within the fan bounds. */
function randomSpread(count: number): CardPos[] {
  return Array.from({ length: count }, () => ({
    x: Math.random() * (FAN_W - CARD_W),
    y: Math.random() * (FAN_H - CARD_H - 16),
    rot: (Math.random() - 0.5) * 36,
  }));
}
